import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { EAnalysisStatus } from 'types/Enums'
import { ETaskStatus } from 'types/GlobalTask'
import { EReduxState } from 'redux/utils/State'

import { useAnalysisTypeActions } from 'redux/actions/AnalysisTypes'
import { useAnalysisActions } from 'redux/actions/Analyses'
import { useTaskActions } from 'redux/actions/Tasks'

import { useTranslation } from 'language/translator'

import { ApplicationState } from 'redux/Stores/types'

import {
  selectAnalysisTypeRealEstates,
  selectAnalysisTypeRealEstatesFetchStatus,
  selectAnalysisType,
  selectAnalysisTypes,
  selectAnalysisTypesFetchStatus
} from 'redux/selectors/AnalysisTypes'
import {
  selectAnalyses,
  selectAnalysesFetchStatus,
  TAnalysisSelectorResponse
} from 'redux/selectors/Analyses'
import { selectTasks, selectTasksFetchStatus } from 'redux/selectors/Tasks'

import { TAnalysisTypeResponse } from 'redux/api/AnalysisTypes'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { TTaskResponse } from 'redux/api/Tasks'

import { IEntity } from 'types/GlobalEntities'

import type {
  TAnalysisTypeDataRequirementsDto,
  TSliceRequirementDto
} from '@homepal/api-contract'

export type TSliceRequirement = TSliceRequirementDto & {
  entity_title: string | null
}

/** An analysis type with the analyses and activities that came of it. */
export type TAnalysisType = Omit<TAnalysisTypeResponse, 'data_requirements'> & {
  data_requirements: Omit<TAnalysisTypeDataRequirementsDto, 'slices'> & {
    slices: TSliceRequirement[]
  }
  analyses: TAnalysisSelectorResponse[]
  tasks: TTaskResponse[]
  discovered_potential: number
  active_potential: number
}

type TFetchState = {
  error: boolean
  loading: boolean
}

const sum = (values: (number | null)[]): number =>
  values.reduce<number>((total, value) => total + (value ?? 0), 0)

/**
 * The open activities of the accepted analyses, and the potential of the
 * unhandled analyses and of the activities. The sums are derived from the
 * fully loaded analysis and activity lists. If either ever paginates, the
 * totals turn into silent undercounts.
 */
const toPotentials = (
  analyses: TAnalysisSelectorResponse[],
  allTasks: TTaskResponse[]
): Pick<
  TAnalysisType,
  'analyses' | 'tasks' | 'discovered_potential' | 'active_potential'
> => {
  const acceptedIds = new Set(
    analyses
      .filter((analysis) => analysis.status === EAnalysisStatus.ACCEPTED)
      .map((analysis) => analysis.id)
  )
  const tasks = allTasks.filter(
    (task) =>
      task.status !== ETaskStatus.DONE &&
      task.source_type === 'ANALYSIS' &&
      task.source_id !== null &&
      acceptedIds.has(task.source_id)
  )
  const unhandled = analyses.filter((analysis) => !analysis.is_handled)

  return {
    active_potential: sum(tasks.map((task) => task.noi_change)),
    discovered_potential: sum(unhandled.map((analysis) => analysis.potential)),
    analyses,
    tasks
  }
}

const toAnalysisType = (
  analysisType: TAnalysisTypeResponse,
  allAnalyses: TAnalysisSelectorResponse[],
  allTasks: TTaskResponse[],
  entities: { [entityId: string]: IEntity },
  lang: 'sv' | 'en'
): TAnalysisType => {
  const { data_requirements: requirements } = analysisType

  return {
    ...analysisType,
    excluded_real_estates: [...analysisType.excluded_real_estates].sort(
      sortAlphabeticalAsc
    ),
    data_requirements: {
      ...requirements,
      slices: requirements.slices.map((slice) => ({
        ...slice,
        entity_title: slice.entity_id
          ? entities[slice.entity_id]?.title[lang] ?? null
          : null
      }))
    },
    ...toPotentials(
      allAnalyses.filter(
        (analysis) => analysis.analysis_type_id === analysisType.id
      ),
      allTasks
    )
  }
}

const isPending = (fetchStatus: string) =>
  fetchStatus === 'idle' || fetchStatus === 'fetching'

/**
 * Fetches what an analysis type is joined with. Each thunk skips a fetch
 * that is loading or done, so every hook can ask for all three.
 */
const useAnalysisTypesFetchState = (): TFetchState => {
  const { tryGetAllAnalysisTypes } = useAnalysisTypeActions()
  const { tryGetAllAnalyses } = useAnalysisActions()
  const { tryGetAllTasks } = useTaskActions()

  const analysisTypesStatus = useSelector(selectAnalysisTypesFetchStatus)
  const analysesStatus = useSelector(selectAnalysesFetchStatus)
  const tasksStatus = useSelector(selectTasksFetchStatus)

  useEffect(() => {
    tryGetAllAnalysisTypes()
    tryGetAllAnalyses()
    tryGetAllTasks()
  }, [tryGetAllAnalysisTypes, tryGetAllAnalyses, tryGetAllTasks])

  return {
    error:
      analysisTypesStatus === 'error' ||
      analysesStatus === 'error' ||
      tasksStatus === EReduxState.ERROR,
    loading:
      isPending(analysisTypesStatus) ||
      isPending(analysesStatus) ||
      tasksStatus === EReduxState.IDLE ||
      tasksStatus === EReduxState.LOADING
  }
}

/** The analysis type list, fetched if it is not in the store yet. */
export const useAnalysisTypes = (): TFetchState & {
  analysisTypes: TAnalysisType[]
} => {
  const analysisTypes = useSelector(selectAnalysisTypes)
  const { lang } = useTranslation()
  const analyses = useSelector((state: ApplicationState) =>
    selectAnalyses(state, lang)
  )
  const tasks = useSelector(selectTasks)
  const entities = useSelector(
    (state: ApplicationState) => state.EntityStore.data
  )
  const { error, loading } = useAnalysisTypesFetchState()

  return {
    error,
    loading,
    analysisTypes: analysisTypes.map((item) =>
      toAnalysisType(item, analyses, tasks, entities, lang)
    )
  }
}

/**
 * The analyses of analysis types that have been retired (no
 * analysis_type_id), which no analysis type in the list carries, with their
 * activities. Shown when there is work left.
 */
export const useRetiredAnalysisTypes = () => {
  const { lang } = useTranslation()
  const analyses = useSelector((state: ApplicationState) =>
    selectAnalyses(state, lang)
  )
  const tasks = useSelector(selectTasks)

  const potentials = toPotentials(
    analyses.filter((analysis) => analysis.analysis_type_id === null),
    tasks
  )

  return {
    ...potentials,
    visible:
      potentials.tasks.length > 0 ||
      potentials.analyses.some((analysis) => !analysis.is_handled)
  }
}

/**
 * The properties an analysis type can exclude, and a fetch to call when they
 * are first needed: it reads the property register, so not on every page.
 */
export const useAnalysisTypeRealEstates = () => {
  const { tryGetAnalysisTypeRealEstates } = useAnalysisTypeActions()
  const realEstates = useSelector(selectAnalysisTypeRealEstates)
  const fetchStatus = useSelector(selectAnalysisTypeRealEstatesFetchStatus)

  return {
    fetch: tryGetAnalysisTypeRealEstates,
    loading: fetchStatus === 'fetching',
    realEstates: [...realEstates].sort(sortAlphabeticalAsc)
  }
}

/** One analysis type by its key, with the same fetch state. */
export const useAnalysisType = (
  key: string | null
): TFetchState & { analysisType: TAnalysisType | null } => {
  const analysisType = useSelector((state: ApplicationState) =>
    selectAnalysisType(state, key)
  )
  const { lang } = useTranslation()
  const analyses = useSelector((state: ApplicationState) =>
    selectAnalyses(state, lang)
  )
  const tasks = useSelector(selectTasks)
  const entities = useSelector(
    (state: ApplicationState) => state.EntityStore.data
  )
  const { error, loading } = useAnalysisTypesFetchState()

  return {
    analysisType: analysisType
      ? toAnalysisType(analysisType, analyses, tasks, entities, lang)
      : null,
    error,
    loading
  }
}
